<template>
  <div class="min-h-screen flex flex-col">
    <ThemedBackground />
    <AppHeader />
    <main class="flex-1 relative z-10">
      <RouterView />
    </main>
    <AppFooter />
    <FeedbackWidget />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useStudentStore } from '@/stores/studentStore';
import { useProgressStore } from '@/stores/progressStore';
import { useAssessmentStore } from '@/stores/assessmentStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import AppHeader from '../components/layout/student/StudentNav.vue';
import AppFooter from '../components/layout/AppFooter.vue';
import ThemedBackground from '../components/ui/ThemedBackground.vue';
import FeedbackWidget from '../components/layout/FeedbackWidget.vue';

const route = useRoute();
const router = useRouter();

const mobileOpen = ref(false);
const userStore = useUserStore();
const studentStore = useStudentStore();
const progressStore = useProgressStore();
const assessmentStore = useAssessmentStore();

const preferencesStore = usePreferencesStore();

// Determine active student from route
const activeStudent = computed(() => {
  return route.params.studentId || studentStore.activeStudent || 'unknown';
});

// Load progress and assessment data when student changes
watch(activeStudent, async (childId) => {
  if (childId && childId !== 'unknown') {
    await Promise.all([
      progressStore.loadProgress(childId),
      assessmentStore.loadAssessments(childId)
    ]);
    preferencesStore.hydrateFromProgress(childId);
  }
}, { immediate: true });

// Also load on mount to ensure data is available
onMounted(async () => {
  if (activeStudent.value && activeStudent.value !== 'unknown') {
    await Promise.all([
      progressStore.loadProgress(activeStudent.value),
      assessmentStore.loadAssessments(activeStudent.value)
    ]);
    preferencesStore.hydrateFromProgress(activeStudent.value);
  }
});

// Sidebar link highlighting
function linkClass(path) {
  return route.path.startsWith(path)
    ? 'bg-brand-50 text-brand-700'
    : 'text-gray-600 hover:bg-gray-100';
}

function logout() {
  userStore.logout();
  router.push('/auth/login');
}
</script>

<style scoped>
/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide Transition */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}
.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}
</style>
